import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { useRoomConnection, clearConnectionLocks } from '../../state/roomStore';
import { GameCanvas } from '../components/GameCanvas';
import { TurnTimeline } from '../components/TurnTimeline';
import { SabotagePanel } from '../components/SabotagePanel';
import { HandSelector } from '../components/HandSelector';
import { useWallet, useConnection } from '@solana/wallet-adapter-react';
import { Transaction, PublicKey, TransactionInstruction } from '@solana/web3.js';
import { Buffer } from 'buffer';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { requestCancelIntent, confirmCancel, SerializedInstruction } from '../../api/matches';
import { MatchChat } from '../components/MatchChat';
import { CompactRunPanel } from '../components/match/CompactRunPanel';
import { PostGameSummary } from '../components/match/PostGameSummary';
import { OpponentJokerPreview } from '../components/match/OpponentJokerPreview';
import { HandHistoryLog } from '../components/match/HandHistoryLog';
import { formatLamports, normalizeLamports, shortKey } from '../../utils/format';
import { useGameEffectsCleanup } from '../../hooks/useGameEffectsCleanup';
import { soundManager } from '../../utils/sounds';
import { sendViaRpc } from '../../wallet/sendViaRpc';
import { saveActiveMatch, touchActiveMatch, clearActiveMatch } from '../../state/matchPersistence';
import { updateMissionProgress } from '../components/DailyMissions';
import { updateSeasonRank } from '../components/SeasonBanner';
import { usePlayerStatsStore } from '../../state/playerStatsStore';
import { useOrientation } from '../../hooks/useMobile';
import { JokerDefinition, describeJokerEffect } from '@solante/shared';
import { RARITY_COLORS, RARITY_BADGE_COLORS } from '../../utils/gameConstants';

// Turn announcement component - dramatic splash with variable duration
const TurnAnnouncement: React.FC<{
  type: 'your_turn' | 'opponent_turn' | 'shop_phase';
  show: boolean;
  onComplete: () => void;
}> = ({ type, show, onComplete }) => {
  // Use ref to avoid timer reset when onComplete reference changes
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    if (show) {
      // opponent_turn is persistent - stays until turn changes
      if (type === 'opponent_turn') return;

      // your_turn is quick (500ms) to not slow player down, shop_phase is 1.5s
      const duration = type === 'your_turn' ? 500 : 1500;
      const timer = setTimeout(() => onCompleteRef.current(), duration);
      return () => clearTimeout(timer);
    }
  }, [show, type]);

  if (!show) return null;

  const config = {
    your_turn: {
      text: 'YOUR TURN',
      color: 'text-arcade-cyan',
      glow: 'drop-shadow-[0_0_20px_rgba(0,255,255,0.8)]',
    },
    opponent_turn: {
      text: 'WAITING FOR OPPONENT',
      color: 'text-amber-400',
      glow: 'drop-shadow-[0_0_15px_rgba(255,180,0,0.6)]',
    },
    shop_phase: {
      text: 'SHOP IS OPEN',
      color: 'text-emerald-400',
      glow: 'drop-shadow-[0_0_15px_rgba(0,255,150,0.6)]',
    },
  }[type];

  return (
    <motion.div
      initial={{ opacity: 0, scale: 2.5 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.8 }}
      transition={{
        duration: 0.15,
        ease: [0.22, 1, 0.36, 1],
      }}
      className="fixed inset-0 z-[60] flex items-center justify-center pointer-events-none pt-safe"
    >
      <motion.div
        initial={{ y: -30 }}
        animate={{ y: 0 }}
        transition={{ type: 'spring', stiffness: 500, damping: 25 }}
        className={`font-display text-4xl md:text-5xl font-bold ${config.color} ${config.glow}`}
      >
        {config.text}
      </motion.div>
    </motion.div>
  );
};

// Score comparison drama component
const ScoreComparisonOverlay: React.FC<{
  myScore: number;
  opponentScore: number;
  show: boolean;
  onComplete: () => void;
}> = ({ myScore, opponentScore, show, onComplete }) => {
  const [displayMyScore, setDisplayMyScore] = useState(0);
  const [displayOpponentScore, setDisplayOpponentScore] = useState(0);
  const [showResult, setShowResult] = useState(false);

  // Use ref to avoid stale closure issues with onComplete
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    if (show) {
      // Animate score count up
      const duration = 1000;
      const steps = 20;
      const stepTime = duration / steps;
      const timeoutIds: NodeJS.Timeout[] = [];

      let step = 0;
      const interval = setInterval(() => {
        step++;
        setDisplayMyScore(Math.round((myScore * step) / steps));
        setDisplayOpponentScore(Math.round((opponentScore * step) / steps));

        if (step >= steps) {
          clearInterval(interval);
          timeoutIds.push(setTimeout(() => setShowResult(true), 300));
          timeoutIds.push(setTimeout(() => onCompleteRef.current(), 2500));
        }
      }, stepTime);

      return () => {
        clearInterval(interval);
        timeoutIds.forEach((id) => clearTimeout(id));
      };
    } else {
      setDisplayMyScore(0);
      setDisplayOpponentScore(0);
      setShowResult(false);
    }
  }, [show, myScore, opponentScore]);

  if (!show) return null;

  const diff = myScore - opponentScore;
  const resultText = diff > 0 ? 'YOU WIN!' : diff < 0 ? 'YOU LOSE' : 'TIE';
  const resultColor = diff > 0 ? 'text-arcade-cyan' : diff < 0 ? 'text-red-400' : 'text-amber-400';
  const nearMiss = Math.abs(diff) < 50 && diff !== 0;
  const dominated = Math.abs(diff) > 200;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 pointer-events-none pt-safe pb-safe"
    >
      <div className="text-center space-y-4">
        <div className="flex items-center justify-center gap-4 sm:gap-8">
          <div className="text-center">
            <div className="text-xs uppercase tracking-widest text-white/50 mb-1">YOU</div>
            <motion.div className="font-display text-3xl font-bold text-arcade-cyan score-slam sm:text-5xl">
              {displayMyScore}
            </motion.div>
          </div>
          <div className="font-display text-xl font-bold text-white/30 vs-flash sm:text-3xl">
            VS
          </div>
          <div className="text-center">
            <div className="text-xs uppercase tracking-widest text-white/50 mb-1">OPP</div>
            <motion.div className="font-display text-3xl font-bold text-red-400 score-slam sm:text-5xl">
              {displayOpponentScore}
            </motion.div>
          </div>
        </div>

        <AnimatePresence>
          {showResult && (
            <motion.div
              initial={{ scale: 0, rotate: -10 }}
              animate={{ scale: 1, rotate: 0 }}
              className={`font-display text-2xl font-bold sm:text-4xl ${resultColor} dramatic-reveal`}
            >
              {resultText}
              {nearMiss && (
                <span className="block text-sm text-amber-300 mt-1 sm:text-lg">SO CLOSE!</span>
              )}
              {dominated && diff > 0 && (
                <span className="block text-lg text-arcade-pink mt-1">DOMINATED!</span>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

const matchStatusCopy: Record<string, string> = {
  open: 'Open',
  awaitingSecondPlayer: 'Awaiting Opponent',
  inProgress: 'In Progress',
  settling: 'Settling',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

const TimerChip: React.FC<{ timeLeft: number; duration: number }> = ({ timeLeft, duration }) => {
  const ratio = duration > 0 ? Math.max(0, Math.min(timeLeft / duration, 1)) : 0;
  const isUrgent = timeLeft <= 5;
  return (
    <motion.div
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      className={`flex items-center gap-2 rounded-full border px-3 py-1 backdrop-blur transition-colors ${
        isUrgent ? 'border-red-400/60 bg-red-500/20 animate-pulse' : 'border-white/10 bg-white/10'
      }`}
    >
      <div className="relative h-1.5 w-16 overflow-hidden rounded-full bg-white/10">
        <motion.div
          className={`absolute inset-y-0 left-0 rounded-full ${isUrgent ? 'bg-red-400' : 'bg-neon-400'}`}
          animate={{ width: `${ratio * 100}%` }}
          transition={{ type: 'spring', stiffness: 160, damping: 24 }}
        />
      </div>
      <span
        className={`font-mono text-xs uppercase tracking-wide ${isUrgent ? 'text-red-300' : 'text-white/70'}`}
      >
        {Math.max(timeLeft, 0)}s
      </span>
    </motion.div>
  );
};

// Opponent activity notification
interface OpponentActivity {
  id: string;
  type: 'purchase' | 'turn';
  message: string;
  timestamp: number;
}

/* ---------- Reusable joker list popup (portrait mode) ---------- */

const JokerListPopup: React.FC<{
  jokers: JokerDefinition[];
  title: string;
  titleColor: string;
  effectColor: string;
  onClose: () => void;
}> = ({ jokers, title, titleColor, effectColor, onClose }) => (
  <div
    className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
    onClick={onClose}
  >
    <div
      className="arcade-panel p-4 max-w-sm max-h-[80vh] overflow-auto"
      onClick={(e) => e.stopPropagation()}
    >
      <h3 className={`font-display text-sm ${titleColor} mb-3`}>{title}</h3>
      {jokers.length > 0 ? (
        <div className="space-y-2">
          {jokers.map((joker) => (
            <div
              key={joker.id}
              className={`rounded-lg border p-2 ${RARITY_COLORS[joker.rarity] ?? RARITY_COLORS['common']}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <span className="font-semibold text-white">{joker.name}</span>
                <span
                  className={`text-[9px] uppercase px-1.5 py-0.5 rounded ${RARITY_BADGE_COLORS[joker.rarity] ?? RARITY_BADGE_COLORS['common']}`}
                >
                  {joker.rarity}
                </span>
              </div>
              <p className="text-xs text-white/60 mb-1">{joker.description}</p>
              {joker.effects.length > 0 && (
                <ul className="space-y-0.5 border-t border-white/10 pt-1.5">
                  {joker.effects.map((effect, i) => (
                    <li key={i} className={`text-xs ${effectColor}`}>
                      + {describeJokerEffect(effect)}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-white/50 text-sm">No jokers equipped</p>
      )}
      <button type="button" className="mt-4 w-full arcade-btn text-sm" onClick={onClose}>
        Close
      </button>
    </div>
  </div>
);

/* ---------- Derived-string helpers (extracted from deep ternary chains) ---------- */

function getTurnDescriptor(
  awaitingSeed: boolean,
  seedCount: number,
  totalPlayers: number,
  isShop: boolean,
  isMyTurn: boolean,
  hasCurrent: boolean,
): string {
  if (awaitingSeed) return `Syncing (${seedCount}/${totalPlayers})`;
  if (isShop) return '🛒 SHOP';
  if (isMyTurn) return '🎯 YOUR TURN';
  if (hasCurrent) return '⏳ OPPONENT';
  return '⏸ WAITING';
}

function getTurnChipClass(isMyTurn: boolean, isShop: boolean): string {
  if (isMyTurn) return 'border-neon-400/60 bg-neon-500/20 text-neon-200 animate-pulse';
  if (isShop) return 'border-emerald-400/40 bg-emerald-500/15 text-emerald-200';
  return 'border-white/10 bg-white/10 text-white/60';
}

function getModalTitle(
  settlement: unknown,
  youAreWinner: boolean,
  isPending: boolean,
  pendingReason?: string,
): string {
  if (settlement) return youAreWinner ? '🏆 VICTORY!' : '💀 DEFEAT';
  if (isPending) {
    if (pendingReason === 'forfeit' && youAreWinner) return 'Opponent Forfeited';
    return 'Settlement Pending';
  }
  return 'Match Settling';
}

function getModalSubtitle(
  settlement: unknown,
  youAreWinner: boolean,
  isPending: boolean,
  pendingReason: string | undefined,
  winnerLabel: string | undefined,
): string {
  if (settlement) {
    return youAreWinner
      ? 'You claimed the wager — well played.'
      : `${winnerLabel ?? 'Opponent'} secured the pot.`;
  }
  if (isPending) {
    if (youAreWinner) return 'Awaiting escrow payout to your wallet.';
    if (pendingReason === 'forfeit')
      return 'You forfeited — escrow will credit your opponent soon.';
    return 'Escrow payout is being processed.';
  }
  return '';
}

export const RoomPage: React.FC = () => {
  const { matchId } = useParams<{ matchId: string }>();
  const {
    connect,
    disconnect,
    error,
    state,
    reconnecting,
    playTurn,
    sendChat,
    purchaseOffer,
    rerollShop,
    useShopConsumable,
    signalShopReady,
    forfeit,
    sendRematchRequest,
    sendSacrifice,
    sendDoubleOrNothingRequest,
  } = useRoomConnection(matchId);
  const { publicKey, signMessage, signTransaction } = useWallet();
  const { connection } = useConnection();
  const queryClient = useQueryClient();
  const wallet = publicKey?.toBase58();

  // Cleanup game effects on unmount
  useGameEffectsCleanup();
  const lastProgressionUpdate = usePlayerStatsStore((s) => s.lastProgressionUpdate);
  const { isPortrait } = useOrientation();
  const [selectedPowerCardId, setSelectedPowerCardId] = useState<string | null>(null);
  const [selectedCards, setSelectedCards] = useState<string[]>([]);
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const [turnDuration, setTurnDuration] = useState<number | null>(null);
  const [forfeitPending, setForfeitPending] = useState(false);
  const [settlementModalVisible, setSettlementModalVisible] = useState(false);
  const [activeSettlementKey, setActiveSettlementKey] = useState<string | null>(null);
  const [opponentActivities, setOpponentActivities] = useState<OpponentActivity[]>([]);
  const [showChat, setShowChat] = useState(false);
  const [showTimeline, setShowTimeline] = useState(false);
  const [showOpponentJokers, setShowOpponentJokers] = useState(false);
  const [showMyJokers, setShowMyJokers] = useState(false);
  const [selectedJoker, setSelectedJoker] = useState<JokerDefinition | null>(null);
  const [turnAnnouncement, setTurnAnnouncement] = useState<
    'your_turn' | 'opponent_turn' | 'shop_phase' | null
  >(null);
  const [activatedJokerIds, setActivatedJokerIds] = useState<string[]>([]);
  const prevTurnWalletRef = useRef<string | null>(null);
  const prevTimelineLength = useRef<number>(0);
  const prevPhaseRef = useRef<string | null>(null);
  const prevOpponentShopRef = useRef<string | null>(null);
  const navigate = useNavigate();
  const settlement = state?.settlement;
  const pendingSettlement = state?.pendingSettlement;
  const opponentDisconnect = state?.opponentDisconnect;
  const playerRunStates = state?.playerRunStates ?? {};
  const playerShopStates = state?.playerShopStates ?? {};
  const opponentPlayer = state?.players?.find((player) => player.wallet !== wallet);
  const myRunState = wallet ? playerRunStates[wallet] : undefined;
  const opponentRunState = opponentPlayer ? playerRunStates[opponentPlayer.wallet] : undefined;
  const myShopState = wallet ? playerShopStates[wallet] : undefined;
  const opponentShopState = opponentPlayer ? playerShopStates[opponentPlayer.wallet] : undefined;
  const canForfeit = state?.status === 'inProgress' && !settlement && !pendingSettlement;

  // Check if user can cancel (waiting for opponent and user is the creator)
  const isCreator = state?.players?.[0]?.wallet === wallet;
  const canCancelMatch =
    state?.status === 'awaitingSecondPlayer' &&
    isCreator &&
    (state?.players?.length ?? 0) === 1 &&
    !settlement &&
    !pendingSettlement;

  const cancelMutation = useMutation({
    mutationFn: async () => {
      if (!publicKey || !matchId) {
        throw new Error('Wallet or match not available');
      }
      if (!connection) {
        throw new Error('Connection unavailable');
      }

      const cancelToastId = toast.loading('Step 1/3: Preparing cancellation…');

      try {
        const walletBase58 = wallet ?? publicKey.toBase58();
        const intent = await requestCancelIntent(matchId, walletBase58);

        toast.loading('Step 2/3: Approve the transaction in your wallet.', {
          id: cancelToastId,
        });

        const instructions = intent.instructions.map(
          (instruction: SerializedInstruction) =>
            new TransactionInstruction({
              programId: new PublicKey(instruction.programId),
              keys: instruction.keys.map((key) => ({
                pubkey: new PublicKey(key.pubkey),
                isSigner: key.isSigner,
                isWritable: key.isWritable,
              })),
              data: Buffer.from(instruction.data, 'base64'),
            }),
        );

        const transaction = new Transaction().add(...instructions);
        transaction.feePayer = publicKey;
        const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash();
        transaction.recentBlockhash = blockhash;

        // Simulate via our RPC first to catch actual program errors
        // (logs the real error instead of Phantom's vague "User rejected")
        const simResult = await connection.simulateTransaction(transaction);
        if (simResult.value.err) {
          console.error('[Cancel] Simulation failed:', JSON.stringify(simResult.value.err));
          console.error('[Cancel] Simulation logs:', simResult.value.logs);
        }

        const signature = await sendViaRpc(transaction, connection, { signTransaction });

        toast.loading('Step 3/3: Confirming cancellation…', {
          id: cancelToastId,
        });

        await connection.confirmTransaction(
          { signature, blockhash, lastValidBlockHeight },
          'confirmed',
        );

        await confirmCancel(matchId, walletBase58, signature);

        toast.success('Match cancelled - funds returned to your wallet!', {
          id: cancelToastId,
          duration: 5000,
        });

        return matchId;
      } catch (caughtError) {
        const normalizedError =
          caughtError instanceof Error
            ? caughtError
            : new Error('Failed to cancel match. Please try again.');
        toast.error(normalizedError.message, { id: cancelToastId });
        (normalizedError as Error & { __toastHandled?: boolean }).__toastHandled = true;
        throw normalizedError;
      }
    },
    onSuccess: async () => {
      soundManager.play('click');
      clearActiveMatch();
      queryClient.invalidateQueries({ queryKey: ['matches'] });
      await disconnect();
      navigate('/matchmaking');
    },
    onError: (error: Error & { __toastHandled?: boolean }) => {
      soundManager.play('error');
      if (!error.__toastHandled) {
        toast.error(error.message);
      }
    },
  });

  // Track turn changes for announcements
  useEffect(() => {
    if (!state?.status || state.status !== 'inProgress') return;

    const currentTurn = state.currentTurnWallet;
    const currentPhase = state.phase;

    // Detect phase change to shop
    if (currentPhase === 'shop' && prevPhaseRef.current !== 'shop') {
      setTurnAnnouncement('shop_phase');
      soundManager.play('coin');
    }
    // Detect turn change (not during shop)
    else if (currentPhase !== 'shop' && currentTurn && currentTurn !== prevTurnWalletRef.current) {
      if (currentTurn === wallet) {
        setTurnAnnouncement('your_turn');
        soundManager.play('click');
      } else if (prevTurnWalletRef.current === wallet) {
        // Only show opponent turn when switching from my turn
        setTurnAnnouncement('opponent_turn');
      }
    }

    prevTurnWalletRef.current = currentTurn ?? null;
    prevPhaseRef.current = currentPhase ?? null;
  }, [state?.currentTurnWallet, state?.phase, state?.status, wallet]);

  const hideTurnAnnouncement = () => setTurnAnnouncement(null);

  // Track turn completion to trigger joker activation animation
  useEffect(() => {
    const timelineLength = state?.timeline?.length ?? 0;
    const lastTurn = state?.timeline?.[timelineLength - 1];

    // Detect new turn added to timeline (turn just completed)
    if (timelineLength > prevTimelineLength.current && lastTurn && myRunState?.jokers?.length) {
      // If the last turn was by the current player, activate their jokers
      if (lastTurn.player === wallet) {
        const jokerIds = myRunState.jokers.map((j) => j.id);
        setActivatedJokerIds(jokerIds);

        // Clear after animation duration
        setTimeout(() => {
          setActivatedJokerIds([]);
        }, 800);
      }
    }

    prevTimelineLength.current = timelineLength;
  }, [state?.timeline, wallet, myRunState?.jokers]);

  // Track opponent shop changes for notifications
  useEffect(() => {
    if (!opponentShopState || !opponentPlayer) return;

    const currentOfferIds = JSON.stringify(opponentShopState.offers.map((o) => o.offerId).sort());

    if (prevOpponentShopRef.current !== null && prevOpponentShopRef.current !== currentOfferIds) {
      // Opponent bought something
      const newActivity: OpponentActivity = {
        id: `${Date.now()}`,
        type: 'purchase',
        message: `${shortKey(opponentPlayer.wallet)} made a purchase`,
        timestamp: Date.now(),
      };
      setOpponentActivities((prev) => [...prev.slice(-4), newActivity]);
      soundManager.play('coin');

      // Auto-remove after 4 seconds
      setTimeout(() => {
        setOpponentActivities((prev) => prev.filter((a) => a.id !== newActivity.id));
      }, 4000);
    }

    prevOpponentShopRef.current = currentOfferIds;
  }, [opponentShopState, opponentPlayer]);

  const handleForfeit = async () => {
    if (!canForfeit) {
      toast.info('Match has not started yet.');
      return;
    }
    try {
      setForfeitPending(true);
      await forfeit();
      toast.warning('You forfeited the match. Awaiting settlement…');
    } catch (forfeitError) {
      toast.error((forfeitError as Error).message);
    } finally {
      setForfeitPending(false);
    }
  };

  const handleLeave = async () => {
    // Only clear active match if game is over or user is forfeiting
    if (state?.status === 'completed' || state?.status === 'cancelled' || settlement) {
      clearActiveMatch();
    }
    await disconnect();
    navigate('/matchmaking');
  };

  const handleReturnToLobby = async () => {
    setSettlementModalVisible(false);
    await handleLeave();
  };

  const handleStayInRoom = () => {
    setSettlementModalVisible(false);
  };

  useEffect(() => {
    if (!matchId || !publicKey || !signMessage) {
      return;
    }
    void connect();
  }, [matchId, publicKey, signMessage, connect]);

  // Persist active match for reconnection on refresh
  useEffect(() => {
    if (!matchId || !wallet) return;

    // Save when joining a match
    if (state?.status === 'inProgress' || state?.status === 'awaitingSecondPlayer') {
      saveActiveMatch({
        matchId,
        wallet,
        wagerAmount: state?.wager?.amountLamports?.toString(),
        currency: state?.wager?.currency,
        joinedAt: Date.now(),
        lastSeenAt: Date.now(),
      });
    }

    // Touch periodically to keep alive
    const touchInterval = setInterval(() => {
      if (state?.status === 'inProgress') {
        touchActiveMatch();
      }
    }, 10000); // Every 10 seconds

    return () => clearInterval(touchInterval);
  }, [matchId, wallet, state?.status, state?.wager]);

  // Clear active match when game completes
  useEffect(() => {
    if (state?.status === 'completed' || state?.status === 'cancelled' || settlement) {
      clearActiveMatch();
    }
  }, [state?.status, settlement]);

  useEffect(() => {
    if (!matchId) {
      return;
    }
    return () => {
      // Clear connection locks and disconnect on unmount
      clearConnectionLocks();
      void disconnect();
    };
  }, [matchId, disconnect]);

  useEffect(() => {
    if (!publicKey) {
      void disconnect();
    }
  }, [publicKey, disconnect]);

  useEffect(() => {
    if (error) {
      toast.error(error.message);
    }
  }, [error]);

  const isShopPhase = state?.phase === 'shop';
  const isMyTurn =
    !isShopPhase && state?.currentTurnWallet ? state.currentTurnWallet === wallet : false;
  const canChat = !!wallet && state?.players?.some((player) => player.wallet === wallet);
  const statusLabel = state?.status ? (matchStatusCopy[state.status] ?? state.status) : 'Loading…';
  const currentTurnDisplay =
    !isShopPhase && state?.currentTurnWallet ? shortKey(state.currentTurnWallet) : null;

  // Check if we're waiting for seed commitments
  const seedCommitments = state?.seedCommitments ?? [];
  const totalPlayers = state?.players?.length ?? 2;
  const awaitingSeedCommitments =
    state?.status === 'awaitingSecondPlayer' ||
    (state?.status !== 'completed' &&
      state?.status !== 'cancelled' &&
      state?.status !== 'settling' &&
      seedCommitments.length < totalPlayers &&
      totalPlayers > 0);

  const turnDescriptor = getTurnDescriptor(
    awaitingSeedCommitments,
    seedCommitments.length,
    totalPlayers,
    isShopPhase,
    isMyTurn,
    !!state?.currentTurnWallet,
  );
  const turnChipClass = getTurnChipClass(isMyTurn, isShopPhase);
  const turnTimerActive = turnDuration !== null && timeLeft !== null && turnDuration > 0;
  const resolutionKey = settlement
    ? `settled:${settlement.matchId}:${settlement.transactionSignature ?? ''}:${settlement.winner}`
    : pendingSettlement
      ? `pending:${pendingSettlement.winner}:${pendingSettlement.reason}`
      : null;
  const parseLamports = (value: unknown): bigint | undefined => {
    if (value === undefined || value === null) {
      return undefined;
    }
    try {
      return normalizeLamports(value);
    } catch {
      return undefined;
    }
  };
  const settlementCurrency = settlement?.currency ?? state?.wager?.currency ?? 'SOL';
  const winnerAmountLamports = settlement ? parseLamports(settlement.winnerAmount) : undefined;
  const treasuryAmountLamports = settlement ? parseLamports(settlement.treasuryAmount) : undefined;
  const totalStakeLamports =
    settlement?.totalStake !== undefined && settlement?.totalStake !== null
      ? parseLamports(settlement.totalStake)
      : winnerAmountLamports !== undefined && treasuryAmountLamports !== undefined
        ? winnerAmountLamports + treasuryAmountLamports
        : undefined;
  const wagerLamports = state?.wager?.amountLamports
    ? parseLamports(state.wager.amountLamports)
    : undefined;
  const winnerAmountDisplay =
    winnerAmountLamports !== undefined
      ? formatLamports(winnerAmountLamports, settlementCurrency)
      : undefined;
  const houseShareDisplay =
    treasuryAmountLamports !== undefined
      ? formatLamports(treasuryAmountLamports, settlementCurrency)
      : undefined;
  const totalPotDisplay =
    totalStakeLamports !== undefined
      ? formatLamports(totalStakeLamports, settlementCurrency)
      : undefined;
  const perPlayerDisplay =
    wagerLamports !== undefined ? formatLamports(wagerLamports, settlementCurrency) : undefined;
  const housePercent =
    totalStakeLamports && treasuryAmountLamports !== undefined && totalStakeLamports > 0n
      ? Number((treasuryAmountLamports * 10000n) / totalStakeLamports) / 100
      : undefined;
  const housePercentDisplay =
    housePercent !== undefined ? `${housePercent.toFixed(2)}%` : undefined;
  const winnerWalletResolved = settlement?.winner ?? pendingSettlement?.winner;
  const winnerPlayer =
    winnerWalletResolved && state?.players
      ? state.players.find((player) => player.wallet === winnerWalletResolved)
      : undefined;
  const winnerLabel = winnerWalletResolved
    ? winnerPlayer?.displayName || shortKey(winnerWalletResolved)
    : undefined;
  const youAreWinner = winnerWalletResolved && wallet ? winnerWalletResolved === wallet : false;
  const isPendingSettlement = !!pendingSettlement && !settlement;
  const modalTitle = getModalTitle(
    settlement,
    youAreWinner,
    isPendingSettlement,
    pendingSettlement?.reason,
  );
  const modalSubtitle = getModalSubtitle(
    settlement,
    youAreWinner,
    isPendingSettlement,
    pendingSettlement?.reason,
    winnerLabel,
  );

  useEffect(() => {
    setSelectedCards([]);
    setSelectedPowerCardId(null);
  }, [state?.currentHandSequence]);

  useEffect(() => {
    if (!state?.turnDeadlineIso) {
      setTimeLeft(null);
      setTurnDuration(null);
      return;
    }
    const deadlineMs = Date.parse(state.turnDeadlineIso);
    if (Number.isNaN(deadlineMs)) {
      setTimeLeft(null);
      setTurnDuration(null);
      return;
    }
    const initialRemaining = Math.max(0, Math.ceil((deadlineMs - Date.now()) / 1000));
    const candidateDuration = state?.turnSecondsRemaining ?? initialRemaining;
    setTurnDuration(candidateDuration > 0 ? candidateDuration : null);
    const update = () => {
      const diff = Math.max(0, Math.ceil((deadlineMs - Date.now()) / 1000));
      setTimeLeft(diff);
    };
    update();
    const interval = window.setInterval(update, 250);
    return () => window.clearInterval(interval);
  }, [state?.turnDeadlineIso, state?.turnSecondsRemaining]);

  useEffect(() => {
    if (!resolutionKey) {
      setSettlementModalVisible(false);
      setActiveSettlementKey(null);
      return;
    }
    if (resolutionKey !== activeSettlementKey) {
      setActiveSettlementKey(resolutionKey);
      setSettlementModalVisible(true);
      // Play sound effect
      if (youAreWinner) {
        soundManager.play('win');
      } else {
        soundManager.play('lose');
      }

      // Update daily mission progress
      updateMissionProgress('play_matches', 1);
      if (youAreWinner) {
        updateMissionProgress('win_matches', 1);
      }

      // Update season rank (ELO change: +25 for win, -20 for loss)
      const eloChange = youAreWinner ? 25 : -20;
      updateSeasonRank(youAreWinner, eloChange);
    }
  }, [resolutionKey, activeSettlementKey, youAreWinner]);

  const handlePlayHand = async (cards: string[], powerCardIds: string[]) => {
    try {
      await playTurn({ playedCardIds: cards, powerCardIds, declaredHand: 'hand' });
      soundManager.play('select');
      setSelectedPowerCardId(null);
      setSelectedCards([]);
    } catch (playError) {
      toast.error((playError as Error).message);
      soundManager.play('error');
      throw playError;
    }
  };

  const handleSelectPower = (cardId: string | null) => {
    if (!isMyTurn) return;
    setSelectedPowerCardId(cardId);
    soundManager.play('click');
  };

  useEffect(() => {
    if (!isMyTurn) {
      setSelectedPowerCardId(null);
      setSelectedCards([]);
    }
  }, [isMyTurn]);

  // Progress towards victory
  const targetScore = state?.targetScore ?? 800;
  const myScore = myRunState?.totalScore ?? 0;
  const opponentScore = opponentRunState?.totalScore ?? 0;
  const turnCount = state?.timeline?.length ?? 0;
  const turnLimit = 25;

  // Show loading state when connecting or state hasn't loaded yet
  const isWalletConnected = !!publicKey;
  // Consider state "ready" when we have the essential data needed for rendering
  const isStateReady = state && state.matchId && state.status;
  const isConnecting = reconnecting || (!isStateReady && isWalletConnected);

  if (!isStateReady && !isWalletConnected) {
    // Wallet not connected - show connect wallet prompt
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="arcade-panel p-8 text-center space-y-6 max-w-md w-full"
        >
          <div className="space-y-2">
            <div className="mx-auto w-16 h-16 rounded-full border-4 border-amber-500/30 flex items-center justify-center text-3xl">
              🔐
            </div>
            <h2 className="font-display text-2xl font-bold text-amber-300">WALLET REQUIRED</h2>
            <p className="text-sm text-white/60">Connect your wallet to join this match</p>
          </div>

          {matchId && (
            <div className="rounded-xl border border-white/10 bg-white/5 p-4">
              <p className="text-xs uppercase tracking-widest text-white/40 mb-1">Match ID</p>
              <p className="font-mono text-neon-300">#{matchId.slice(0, 8)}...</p>
            </div>
          )}

          <button
            type="button"
            onClick={() => navigate('/matchmaking')}
            className="arcade-btn w-full"
          >
            Return to Matchmaking
          </button>
        </motion.div>
      </div>
    );
  }

  if (isConnecting) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="arcade-panel p-8 text-center space-y-6 max-w-md w-full"
        >
          <div className="space-y-2">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
              className="mx-auto w-16 h-16 rounded-full border-4 border-neon-500/30 border-t-neon-400"
            />
            <h2 className="font-display text-2xl font-bold neon-text">
              {reconnecting ? 'RECONNECTING...' : 'JOINING MATCH'}
            </h2>
            <p className="text-sm text-white/60">
              {reconnecting
                ? 'Reconnecting to the match room...'
                : 'Establishing secure connection...'}
            </p>
          </div>

          {matchId && (
            <div className="rounded-xl border border-white/10 bg-white/5 p-4">
              <p className="text-xs uppercase tracking-widest text-white/40 mb-1">Match ID</p>
              <p className="font-mono text-neon-300">#{matchId.slice(0, 8)}...</p>
            </div>
          )}

          {error && (
            <div className="rounded-xl border border-red-400/30 bg-red-500/10 p-4">
              <p className="text-sm text-red-300">{error.message}</p>
              <button type="button" onClick={() => connect()} className="mt-3 arcade-btn text-sm">
                Retry Connection
              </button>
            </div>
          )}

          <p className="text-xs text-white/40">✦ Securing provably fair seeds...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div
      className={`flex-1 min-h-0 flex flex-col overflow-hidden ${isPortrait ? 'pt-0' : 'pt-14'}`}
    >
      {/* Settlement Modal with Post-Game Summary */}
      {(settlement || isPendingSettlement) && settlementModalVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4 pt-safe pb-safe">
          <PostGameSummary
            isWinner={youAreWinner}
            myScore={myScore}
            opponentScore={opponentScore}
            evolutions={state?.evolutions ?? []}
            myWallet={wallet}
            totalPot={totalPotDisplay}
            winnerPayout={winnerAmountDisplay}
            currency={settlementCurrency}
            onPlayAgain={handleReturnToLobby}
            onMainMenu={handleReturnToLobby}
            onRematchRequest={sendRematchRequest}
            rematchRequested={wallet ? state?.rematchRequests?.includes(wallet) : false}
            opponentRematchRequested={state?.rematchRequests?.some((w) => w !== wallet) ?? false}
            rematchConfirmed={state?.rematchConfirmed}
            onDoubleOrNothingRequest={sendDoubleOrNothingRequest}
            doubleOrNothingProposed={state?.doubleOrNothingProposed}
            doubleOrNothingProposedByMe={
              wallet ? state?.doubleOrNothingProposedBy === wallet : false
            }
            doubleOrNothingConfirmed={state?.doubleOrNothingConfirmed}
            doubleOrNothingWagerAmount={
              state?.doubleOrNothingWager?.amountLamports
                ? formatLamports(
                    normalizeLamports(state.doubleOrNothingWager.amountLamports),
                    state.doubleOrNothingWager.currency || 'SOL',
                  )
                : undefined
            }
            xpGained={
              lastProgressionUpdate && lastProgressionUpdate.wallet === wallet
                ? lastProgressionUpdate.xpGained
                : undefined
            }
            eloChange={
              lastProgressionUpdate && lastProgressionUpdate.wallet === wallet
                ? lastProgressionUpdate.eloDelta
                : undefined
            }
            newElo={
              lastProgressionUpdate && lastProgressionUpdate.wallet === wallet
                ? lastProgressionUpdate.newElo
                : undefined
            }
            badBeatBonus={
              lastProgressionUpdate && lastProgressionUpdate.wallet === wallet
                ? lastProgressionUpdate.badBeatBonus
                : undefined
            }
          />
        </div>
      )}

      {/* Turn Announcement Overlay */}
      <AnimatePresence>
        {turnAnnouncement && (
          <TurnAnnouncement
            type={turnAnnouncement}
            show={!!turnAnnouncement}
            onComplete={hideTurnAnnouncement}
          />
        )}
      </AnimatePresence>

      {/* Opponent Activity Notifications */}
      <AnimatePresence>
        {opponentActivities.map((activity) => (
          <motion.div
            key={activity.id}
            initial={{ opacity: 0, x: 100, y: 0 }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            exit={{ opacity: 0, x: 100 }}
            className="fixed top-4 right-4 z-50 rounded-lg border border-amber-400/50 bg-amber-500/20 px-4 py-2 text-sm text-amber-200 backdrop-blur sm:top-20"
          >
            🛒 {activity.message}
          </motion.div>
        ))}
      </AnimatePresence>

      {/* Opponent Disconnect Banner */}
      <AnimatePresence>
        {opponentDisconnect && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-2 left-1/2 -translate-x-1/2 z-50 arcade-panel px-4 py-2 rounded-xl border border-amber-500/60 max-w-[90vw] sm:top-20 sm:px-6 sm:py-3"
          >
            <div className="flex items-center gap-3">
              <span className="text-amber-400 animate-pulse text-lg">⚠</span>
              <div>
                <p className="text-amber-300 font-display text-sm">OPPONENT DISCONNECTED</p>
                <p className="text-white/60 text-xs">
                  Auto-forfeit in {Math.ceil(opponentDisconnect.remainingTime / 1000)}s
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content Area - Flex layout to fill screen */}
      <div className="flex-1 flex flex-col gap-2 p-2 overflow-hidden">
        {/* Top Info Bar - Portrait: ultra-compact, Desktop: full */}
        {isPortrait ? (
          <div className="flex items-center justify-between gap-1 rounded-lg border border-white/10 bg-black/40 px-2 py-1 backdrop-blur shrink-0 pl-safe pr-safe">
            {/* Scores */}
            <div className="flex items-center gap-1 text-sm">
              <span className="font-bold text-neon-400">{myScore}</span>
              <span className="text-white/30 text-xs">vs</span>
              <span className="font-bold text-red-400">{opponentScore}</span>
            </div>

            {/* Your stats (tappable to show jokers) */}
            <button
              type="button"
              className="flex items-center gap-1 text-xs"
              onClick={() => setShowMyJokers(true)}
            >
              <span className="text-amber-300">⛁</span>
              <span className="font-bold text-white">{myRunState?.chips ?? 0}</span>
              <span className="text-rose-300 ml-1">♥</span>
              <span className="font-bold text-white">{myRunState?.hearts ?? 0}</span>
            </button>

            {/* Opponent stats (tappable) */}
            <button
              type="button"
              className="flex items-center gap-1 text-xs opacity-60"
              onClick={() => setShowOpponentJokers(true)}
            >
              <span className="text-amber-300">⛁</span>
              <span className="font-bold text-white">{opponentRunState?.chips ?? 0}</span>
              <span className="text-rose-300 ml-1">♥</span>
              <span className="font-bold text-white">{opponentRunState?.hearts ?? 0}</span>
            </button>

            {/* Turn indicator + timer */}
            <div className="flex items-center gap-1">
              <span className={`text-xs font-bold ${isMyTurn ? 'text-neon-400' : 'text-white/40'}`}>
                {isShopPhase ? '🛒' : isMyTurn ? '🎯' : '⏳'}
              </span>
              {timeLeft !== null && (
                <span
                  className={`text-xs font-mono ${timeLeft <= 5 ? 'text-red-400 animate-pulse' : 'text-white/60'}`}
                >
                  {timeLeft}s
                </span>
              )}
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-2 rounded-lg border border-white/10 bg-black/40 px-2 md:px-3 py-1.5 backdrop-blur shrink-0 pl-safe pr-safe">
            {/* Left: Match & Turn */}
            <div className="flex items-center gap-2 md:gap-3 min-w-0">
              <span className="font-display text-xs md:text-sm font-bold text-white shrink-0">
                #{matchId?.slice(0, 6)}
              </span>
              <span
                className={`rounded-full border px-1.5 md:px-2 py-0.5 text-[10px] md:text-xs font-bold uppercase shrink-0 ${turnChipClass}`}
              >
                {turnDescriptor}
              </span>
              {turnTimerActive && turnDuration && timeLeft !== null && (
                <div className="hidden sm:block">
                  <TimerChip timeLeft={timeLeft} duration={turnDuration} />
                </div>
              )}
              {/* Mobile timer - simplified */}
              {turnTimerActive && timeLeft !== null && (
                <span
                  className={`sm:hidden text-xs font-mono ${timeLeft <= 5 ? 'text-red-400 animate-pulse' : 'text-white/70'}`}
                >
                  {timeLeft}s
                </span>
              )}
              {(reconnecting || awaitingSeedCommitments) && (
                <span className="hidden sm:flex items-center gap-1 text-xs text-amber-300">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-amber-300" />
                  {reconnecting ? 'Reconnecting...' : 'Syncing...'}
                </span>
              )}
            </div>

            {/* Center: Score Battle - Responsive */}
            <div className="flex items-center gap-2 md:gap-4">
              <div className="flex items-center gap-1 md:gap-2">
                <span className="hidden md:block text-xs text-white/50">YOU</span>
                <span className="text-base md:text-lg font-bold text-neon-400">{myScore}</span>
                <div className="hidden md:block h-1 w-16 overflow-hidden rounded-full bg-white/10">
                  <div
                    className="h-full bg-neon-400 transition-all"
                    style={{ width: `${Math.min(100, (myScore / targetScore) * 100)}%` }}
                  />
                </div>
              </div>
              <span className="text-[10px] md:text-xs font-bold text-white/30">VS</span>
              <div className="flex items-center gap-1 md:gap-2">
                <div className="hidden md:block h-1 w-16 overflow-hidden rounded-full bg-white/10">
                  <div
                    className="h-full bg-red-400 transition-all"
                    style={{ width: `${Math.min(100, (opponentScore / targetScore) * 100)}%` }}
                  />
                </div>
                <span className="text-base md:text-lg font-bold text-red-400">{opponentScore}</span>
                <span className="hidden md:block text-xs text-white/50">OPP</span>
              </div>
              <span className="text-[10px] md:text-xs text-white/40">
                T{turnCount}/{turnLimit}
              </span>
            </div>

            {/* Right: Quick Stats */}
            <div className="flex items-center gap-2 md:gap-3 text-xs shrink-0">
              <span className="flex items-center gap-1">
                <span className="text-amber-300">⛁</span>
                <span className="font-bold text-white">{myRunState?.chips ?? 0}</span>
              </span>
              <span className="flex items-center gap-1">
                <span className="text-rose-300">♥</span>
                <span className="font-bold text-white">{myRunState?.hearts ?? 0}</span>
              </span>
            </div>
          </div>
        )}

        {/* Main Game Grid - Responsive: single column on mobile, 2 columns on desktop */}
        <div className="flex-1 flex flex-col lg:grid lg:grid-cols-[1fr,minmax(220px,280px)] gap-2 min-h-0 overflow-hidden pl-safe pr-safe">
          {/* Left: Game Canvas + Hand */}
          <div className="flex-1 flex flex-col gap-2 min-h-0">
            {/* Game Canvas - Flexible height */}
            <div className="flex-1 min-h-0">
              <GameCanvas
                state={state}
                selectedCardIds={selectedCards}
                timeRemainingSeconds={timeLeft ?? undefined}
                jokers={myRunState?.jokers ?? []}
                isInteractive={isMyTurn}
                activatedJokerIds={activatedJokerIds}
                onCardClick={(cardId) => {
                  if (!isMyTurn) return;
                  const isSelected = selectedCards.includes(cardId);
                  if (isSelected) {
                    soundManager.play('click');
                    setSelectedCards(selectedCards.filter((id) => id !== cardId));
                  } else {
                    if (selectedCards.length >= 5) {
                      soundManager.play('error');
                      return;
                    }
                    soundManager.play('select');
                    setSelectedCards([...selectedCards, cardId]);
                  }
                }}
              />
            </div>

            {/* Hand Selector + Sabotage - Desktop only */}
            <div className="hidden lg:grid grid-cols-[1fr,minmax(180px,200px)] gap-2 h-[56px] shrink-0">
              <HandSelector
                state={state}
                isMyTurn={isMyTurn}
                selectedPowerCardId={selectedPowerCardId}
                selectedCardIds={selectedCards}
                onSelectionChange={setSelectedCards}
                onPlay={handlePlayHand}
                timeRemainingSeconds={timeLeft ?? undefined}
              />
              <SabotagePanel
                state={state}
                selectedCardId={selectedPowerCardId}
                onSelect={handleSelectPower}
                isMyTurn={isMyTurn}
                onSacrifice={sendSacrifice}
              />
            </div>

            {/* My Jokers Row - Portrait only */}
            {isPortrait && myRunState?.jokers && myRunState.jokers.length > 0 && (
              <div className="lg:hidden shrink-0 flex items-center gap-2 px-2 py-1 overflow-x-auto bg-black/30 rounded-lg border border-arcade-cyan/20">
                <span className="text-[10px] text-arcade-cyan uppercase shrink-0">Jokers:</span>
                {myRunState.jokers.map((joker) => (
                  <button
                    key={joker.id}
                    type="button"
                    onClick={() => setSelectedJoker(joker)}
                    className={`shrink-0 rounded-lg border px-2 py-1 text-[11px] font-semibold transition hover:scale-105 ${
                      RARITY_COLORS[joker.rarity] ?? RARITY_COLORS['common']
                    }`}
                  >
                    {joker.name}
                  </button>
                ))}
              </div>
            )}

            {/* Mobile: Hand Selector only (full width) - compact in portrait */}
            <div className={`lg:hidden shrink-0 ${isPortrait ? 'h-[44px]' : 'h-[52px]'}`}>
              <HandSelector
                state={state}
                isMyTurn={isMyTurn}
                selectedPowerCardId={selectedPowerCardId}
                selectedCardIds={selectedCards}
                onSelectionChange={setSelectedCards}
                onPlay={handlePlayHand}
                timeRemainingSeconds={timeLeft ?? undefined}
              />
            </div>
          </div>

          {/* Right Sidebar - Desktop only */}
          <div className="hidden lg:flex flex-col gap-2 min-h-0 overflow-y-auto">
            {/* Opponent Info */}
            {opponentPlayer && (
              <div className="arcade-panel p-2 shrink-0">
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-widest text-red-400">
                    {shortKey(opponentPlayer.wallet)}
                  </span>
                  <div className="flex items-center gap-2 text-xs">
                    <span className="flex items-center gap-1">
                      <span className="text-amber-300/60">⛁</span>
                      <span className="font-bold text-white/70">
                        {opponentRunState?.chips ?? 0}
                      </span>
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="text-rose-300/60">♥</span>
                      <span className="font-bold text-white/70">
                        {opponentRunState?.hearts ?? 0}
                      </span>
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Opponent Joker Preview - Shows opponent's equipped jokers for counterplay */}
            {opponentPlayer && opponentRunState?.jokers && opponentRunState.jokers.length > 0 && (
              <OpponentJokerPreview
                jokers={opponentRunState.jokers}
                opponentName={shortKey(opponentPlayer.wallet)}
              />
            )}

            {/* Your Loadout (Shop) - Flex grows */}
            <div className="flex-1 min-h-0 overflow-auto">
              <CompactRunPanel
                runState={myRunState}
                shopState={myShopState}
                phase={state?.phase}
                onBuy={purchaseOffer}
                onReroll={rerollShop}
                onUseConsumable={useShopConsumable}
                onReady={signalShopReady}
              />
            </div>

            {/* Chat - Collapsible */}
            <AnimatePresence>
              {showChat && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 180 }}
                  exit={{ opacity: 0, height: 0 }}
                  className="arcade-panel p-2 shrink-0 overflow-hidden"
                >
                  <MatchChat
                    messages={state?.chatMessages ?? []}
                    onSend={sendChat}
                    disabled={!canChat}
                    currentWallet={wallet}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Bottom Bar - Responsive controls, compact in portrait */}
        <div
          className={`flex items-center justify-between gap-2 md:gap-3 rounded-lg border border-white/10 bg-black/40 px-2 md:px-3 backdrop-blur shrink-0 pl-safe pr-safe pb-safe ${isPortrait ? 'py-1' : 'py-1.5'}`}
        >
          {/* Left: Wager - compact inline on desktop, hidden in portrait */}
          <div className="flex items-center gap-2 min-w-0">
            {!isPortrait && state?.wager && (
              <div className="flex items-center gap-3 text-xs text-white/60">
                <span>
                  Pot:{' '}
                  <span className="font-semibold text-neon-400">
                    {formatLamports(
                      normalizeLamports(state.wager.amountLamports),
                      state.wager.currency,
                    )}
                  </span>
                </span>
                <span className="text-white/30">|</span>
                <span>
                  {state?.escrowedWallets?.length ?? 0}/{state?.players?.length ?? 2} locked
                </span>
              </div>
            )}
          </div>

          {/* Center: Timeline Toggle */}
          <button
            type="button"
            onClick={() => setShowTimeline(!showTimeline)}
            className="flex items-center justify-center gap-1 rounded border border-white/20 bg-white/5 min-h-[36px] min-w-[36px] px-2 py-1 text-xs text-white/70 hover:bg-white/10 transition"
            title={showTimeline ? 'Hide History' : 'Show History'}
          >
            📜 <span className="hidden sm:inline">{showTimeline ? 'HIDE' : 'HISTORY'}</span>
          </button>

          {/* Right: Actions */}
          <div className="flex items-center gap-1 md:gap-2">
            {/* Portrait: Back button since no navbar */}
            {isPortrait && (
              <button
                type="button"
                onClick={handleLeave}
                className="rounded border border-white/20 bg-white/5 min-h-[36px] px-2 py-1 text-xs text-white/70 hover:bg-white/10"
                title="Leave Match"
              >
                ←
              </button>
            )}
            <button
              type="button"
              onClick={() => setShowChat(!showChat)}
              className={`rounded border min-h-[36px] min-w-[36px] px-2 py-1 text-xs transition ${showChat ? 'border-neon-400/60 bg-neon-500/20 text-neon-200' : 'border-white/20 bg-white/5 text-white/70 hover:bg-white/10'}`}
              title="Toggle Chat"
            >
              💬
            </button>
            {canCancelMatch && (
              <button
                type="button"
                onClick={() => cancelMutation.mutate()}
                disabled={cancelMutation.isPending || reconnecting}
                className="rounded border border-amber-400/40 bg-amber-500/10 min-h-[36px] px-2 md:px-3 py-1 text-xs font-semibold uppercase text-amber-300 transition hover:bg-amber-500/20 disabled:cursor-not-allowed disabled:opacity-50"
                title="Cancel Match"
              >
                <span className="hidden sm:inline">
                  {cancelMutation.isPending ? 'Cancelling…' : 'Cancel'}
                </span>
                <span className="sm:hidden">🚫</span>
              </button>
            )}
            <button
              type="button"
              onClick={handleForfeit}
              disabled={!canForfeit || reconnecting || forfeitPending}
              className="rounded border border-rose-400/40 bg-rose-500/10 min-h-[36px] min-w-[36px] px-2 py-1 text-xs font-semibold uppercase text-rose-300 transition hover:bg-rose-500/20 disabled:cursor-not-allowed disabled:opacity-50"
              title="Forfeit Match"
            >
              {forfeitPending ? '...' : '🏳️'}
            </button>
            <button
              type="button"
              onClick={handleLeave}
              disabled={reconnecting || forfeitPending || cancelMutation.isPending}
              className="rounded border border-white/20 bg-white/5 min-h-[36px] min-w-[36px] px-2 py-1 text-xs font-semibold text-white/70 transition hover:bg-white/10 disabled:opacity-50"
              title="Leave Room"
            >
              ✖
            </button>
          </div>
        </div>
      </div>

      {/* Hand History Overlay */}
      <AnimatePresence>
        {showTimeline && (
          <motion.div
            initial={{ opacity: 0, y: 20, x: '-50%' }}
            animate={{ opacity: 1, y: 0, x: '-50%' }}
            exit={{ opacity: 0, y: 20, x: '-50%' }}
            className="fixed bottom-20 left-1/2 z-40 arcade-panel p-0 overflow-hidden mb-safe sm:bottom-16"
            style={{ width: 'min(90vw, 600px)', height: 'min(50vh, 350px)' }}
          >
            <div className="flex items-center justify-between px-3 pt-2">
              <h3 className="text-xs uppercase tracking-widest text-white/50">Hand History</h3>
              <button
                type="button"
                onClick={() => setShowTimeline(false)}
                className="text-white/50 hover:text-white text-xs"
              >
                ✖
              </button>
            </div>
            <div className="h-[calc(100%-30px)]">
              <HandHistoryLog history={state?.timeline ?? []} myWallet={wallet} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile Chat Overlay */}
      {showChat && isPortrait && (
        <div className="fixed inset-0 z-[9999] bg-black flex flex-col px-3 pt-20 pb-safe">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-display text-lg text-neon-400">CHAT</h2>
            <button
              type="button"
              onClick={() => setShowChat(false)}
              className="flex items-center justify-center rounded-lg border border-rose-400/50 bg-rose-500/20 min-h-[44px] px-4 py-2 text-sm font-bold text-rose-300 hover:bg-rose-500/30 active:bg-rose-500/40 transition"
            >
              CLOSE ✕
            </button>
          </div>
          <div className="flex-1 overflow-hidden">
            <MatchChat
              messages={state?.chatMessages ?? []}
              onSend={sendChat}
              disabled={!canChat}
              currentWallet={wallet}
            />
          </div>
        </div>
      )}

      {/* Mobile Shop Overlay - auto-shows during shop phase */}
      {isPortrait && isShopPhase && (
        <div className="fixed inset-0 z-50 bg-black/90 flex flex-col p-3 pt-safe pb-safe">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-lg text-arcade-cyan">🛒 SHOP</h2>
            {timeLeft !== null && (
              <span className="text-sm text-white/60">{timeLeft}s remaining</span>
            )}
          </div>
          <div className="flex-1 overflow-auto">
            <CompactRunPanel
              runState={myRunState}
              shopState={myShopState}
              phase={state?.phase}
              onBuy={purchaseOffer}
              onReroll={rerollShop}
              onUseConsumable={useShopConsumable}
              onReady={signalShopReady}
            />
          </div>
        </div>
      )}

      {/* Opponent Jokers Popup - tap to reveal */}
      {showOpponentJokers && isPortrait && (
        <JokerListPopup
          jokers={opponentRunState?.jokers ?? []}
          title="OPPONENT'S JOKERS"
          titleColor="text-red-400"
          effectColor="text-red-400"
          onClose={() => setShowOpponentJokers(false)}
        />
      )}

      {/* My Jokers Popup - tap stats to reveal */}
      {showMyJokers && isPortrait && (
        <JokerListPopup
          jokers={myRunState?.jokers ?? []}
          title="YOUR JOKERS"
          titleColor="text-arcade-cyan"
          effectColor="text-arcade-cyan"
          onClose={() => setShowMyJokers(false)}
        />
      )}

      {/* Joker Detail Popup - tap joker to see effects */}
      {selectedJoker && isPortrait && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setSelectedJoker(null)}
        >
          <div className="arcade-panel p-4 max-w-sm" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-2">
              <span className="font-display text-lg text-white">{selectedJoker.name}</span>
              <span
                className={`text-[10px] uppercase px-2 py-0.5 rounded ${RARITY_BADGE_COLORS[selectedJoker.rarity] ?? RARITY_BADGE_COLORS['common']}`}
              >
                {selectedJoker.rarity}
              </span>
            </div>
            <p className="text-sm text-white/70 mb-3">{selectedJoker.description}</p>
            {selectedJoker.effects.length > 0 && (
              <div className="border-t border-white/10 pt-2">
                <span className="text-[10px] uppercase text-white/40">Effects:</span>
                <ul className="mt-1 space-y-1">
                  {selectedJoker.effects.map((effect, i) => (
                    <li key={i} className="text-sm text-arcade-cyan">
                      + {describeJokerEffect(effect)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <button
              type="button"
              className="mt-4 w-full arcade-btn text-sm"
              onClick={() => setSelectedJoker(null)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
